import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChatCircle, PaperPlaneTilt, ArrowLeft, User, Users, MagnifyingGlass } from "@phosphor-icons/react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Chat = () => {
  const { user, getAuthHeaders } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [showMembers, setShowMembers] = useState(false);
  const [search, setSearch] = useState("");
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const h = { headers: getAuthHeaders(), withCredentials: true };

  useEffect(() => {
    fetchRooms();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const fetchRooms = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/chat/rooms`, h);
      setRooms(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openRoom = async (room) => {
    setActiveRoom(room);
    if (pollRef.current) clearInterval(pollRef.current);
    await fetchMessages(room.id);
    pollRef.current = setInterval(() => fetchMessages(room.id), 3000);
  };

  const fetchMessages = async (roomId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/chat/${roomId}/messages`, h);
      setMessages(res.data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) { console.error(e); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activeRoom) return;
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/chat/${activeRoom.id}/messages`, { content: newMsg }, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, withCredentials: true,
      });
      setNewMsg("");
      await fetchMessages(activeRoom.id);
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const startDM = async (memberId) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/chat/direct`, { target_user_id: memberId }, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, withCredentials: true,
      });
      setShowMembers(false);
      await fetchRooms();
      openRoom({ id: res.data.id, name: "", type: "direct" });
    } catch (e) { console.error(e); }
  };

  const loadMembers = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/chat/members`, h);
      setMembers(res.data.filter((m) => m.id !== user?.id));
    } catch (e) { console.error(e); }
    setShowMembers(true);
  };

  const groupRooms = rooms.filter((r) => r.type === "group");
  const directRooms = rooms.filter((r) => r.type === "direct");
  const filteredMembers = members.filter((m) => m.name.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="text-center py-12 text-[#115E59]">Carregando chat...</div>;

  // Chat room view
  if (activeRoom) {
    return (
      <div className="flex flex-col" style={{ height: "calc(100vh - 220px)" }} data-testid="chat-room">
        {/* Room header */}
        <div className="flex items-center gap-3 pb-4 border-b border-[#E4DFD3]">
          <Button onClick={() => { setActiveRoom(null); if (pollRef.current) clearInterval(pollRef.current); fetchRooms(); }}
            variant="ghost" className="rounded-full shrink-0" data-testid="chat-back">
            <ArrowLeft size={22} />
          </Button>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeRoom.type === "direct" ? "bg-[#0891B2]" : "bg-[#FF5A26]"}`}>
              {activeRoom.type === "direct" ? <User size={20} weight="bold" className="text-white" /> : <Users size={20} weight="bold" className="text-white" />}
            </div>
            <h3 className="font-bold text-[#042F2E] text-lg">{activeRoom.name}</h3>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto py-4 space-y-3 min-h-0">
          {messages.length === 0 && (
            <p className="text-center text-[#115E59] py-8">Nenhuma mensagem ainda. Seja o primeiro!</p>
          )}
          {messages.map((msg) => {
            const isMe = msg.user_id === user?.id;
            return (
              <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`msg-${msg.id}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                  isMe ? "bg-[#FF5A26] text-white rounded-br-md" : "bg-white border border-[#E4DFD3] text-[#042F2E] rounded-bl-md"
                }`}>
                  {!isMe && <p className="text-xs font-bold mb-1 opacity-70">{msg.user_name}</p>}
                  <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                  <p className={`text-[10px] mt-1 ${isMe ? "text-white/60" : "text-[#115E59]/50"}`}>
                    {new Date(msg.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={sendMessage} className="flex gap-2 pt-3 border-t border-[#E4DFD3]">
          <Input value={newMsg} onChange={(e) => setNewMsg(e.target.value)}
            placeholder="Digite sua mensagem..." className="rounded-full border-[#E4DFD3] flex-1"
            data-testid="chat-input" autoFocus />
          <Button type="submit" disabled={sending || !newMsg.trim()}
            className="bg-[#FF5A26] hover:bg-[#E04818] text-white rounded-full w-12 h-10 p-0 shrink-0"
            data-testid="chat-send">
            <PaperPlaneTilt size={20} weight="fill" />
          </Button>
        </form>
      </div>
    );
  }

  // Room list view
  return (
    <div data-testid="chat-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#042F2E] mb-1">Chat</h2>
          <p className="text-[#115E59]">Converse com outros membros do clube</p>
        </div>
        <Button onClick={loadMembers} className="bg-[#0891B2] hover:bg-[#0E7490] text-white font-bold rounded-full px-5"
          data-testid="new-dm-btn">
          <ChatCircle size={18} weight="bold" className="mr-2" /> Nova Conversa
        </Button>
      </div>

      {/* Member selector for DM */}
      <AnimatePresence>
        {showMembers && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-2xl border border-[#E4DFD3] p-4 mb-6 overflow-hidden" data-testid="member-selector">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#042F2E]">Iniciar conversa com:</h3>
              <Button onClick={() => setShowMembers(false)} variant="ghost" className="rounded-full text-xs">Fechar</Button>
            </div>
            <div className="relative mb-3">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#115E59]" size={18} />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar membro..." className="pl-10 rounded-xl border-[#E4DFD3]" data-testid="search-members" />
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {filteredMembers.map((m) => (
                <button key={m.id} onClick={() => startDM(m.id)}
                  className="w-full text-left px-4 py-2.5 rounded-xl hover:bg-[#F4F1EA] flex items-center gap-3 transition-colors"
                  data-testid={`dm-member-${m.id}`}>
                  <div className="w-8 h-8 bg-[#0891B2] rounded-full flex items-center justify-center">
                    <User size={16} weight="bold" className="text-white" />
                  </div>
                  <span className="font-semibold text-[#042F2E] text-sm">{m.name}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Group chats */}
      <h3 className="text-sm font-bold text-[#115E59] uppercase tracking-wider mb-3">Grupos</h3>
      <div className="space-y-2 mb-8">
        {groupRooms.map((room) => (
          <button key={room.id} onClick={() => openRoom(room)}
            className="w-full bg-white rounded-xl border border-[#E4DFD3] p-4 flex items-center gap-4 hover:shadow-md transition-all text-left"
            data-testid={`room-${room.id}`}>
            <div className="w-12 h-12 bg-[#FF5A26] rounded-xl flex items-center justify-center shrink-0">
              <Users size={22} weight="bold" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[#042F2E]">{room.name}</p>
              <p className="text-xs text-[#115E59] truncate">
                {room.last_message ? `${room.last_message_user}: ${room.last_message}` : "Nenhuma mensagem ainda"}
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* Direct messages */}
      {directRooms.length > 0 && (
        <>
          <h3 className="text-sm font-bold text-[#115E59] uppercase tracking-wider mb-3">Conversas Diretas</h3>
          <div className="space-y-2">
            {directRooms.map((room) => (
              <button key={room.id} onClick={() => openRoom(room)}
                className="w-full bg-white rounded-xl border border-[#E4DFD3] p-4 flex items-center gap-4 hover:shadow-md transition-all text-left"
                data-testid={`room-${room.id}`}>
                <div className="w-12 h-12 bg-[#0891B2] rounded-xl flex items-center justify-center shrink-0">
                  <User size={22} weight="bold" className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#042F2E]">{room.name}</p>
                  <p className="text-xs text-[#115E59] truncate">
                    {room.last_message ? `${room.last_message_user}: ${room.last_message}` : "Inicie a conversa!"}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Chat;
